/**
 * Monthly Mortgage Payment Calculator
 *
 * Interactions for JS calculators
 *
 * Tasks discussed with rob 7/26
 *
 * 1. Add insurance
 * 2. Add PMI
 * 3. Debug safari, browser test and tighten up Bulma
 * 4. Add in the total monthly payment in the middle of pie chart
 * 5. Make fully responsive
 * 6. WCAG scan for accessibility
 *
 * Data sources:
 *
 * MA Taxes: https://dlsgateway.dor.state.ma.us/reports/rdPage.aspx?rdReport=PropertyTaxInformation.taxratesbyclass.taxratesbyclass_main
 * RI Taxes: http://www.municipalfinance.ri.gov/documents/data/taxrates/2018-Tax-Rates-12-31-17-FINAL.pdf
 *
 * REGEX Notes:
 *
 * MA Excel Pattern:
 * RI PDF Pattern
 *   Find: ([A-Z\s]+)\s([0-9\.]+)\s([0-9\.\s]+)\n?
 *   Replace: { city: "$1", propertyTax: $2, state: "RI" },
 *
 * DEBUG URL
 * https://coastal1.test/mortgage-calculator-custom/?logged-out&home-price=520000&down-payment=106000&loan-term=30&zip-code=02030&lead-name=Kevin Leary&lead-email=coastal1@kevinleary.net&lead-phone=7815901000&lead-car-loans=500&lead-student-loans=1000&lead-credit-cards=75&lead-personal-loans=250
 */
import { parseInt, round, toNumber, sum, some, isEmpty, lt, isEqual, includes, isNumber } from "lodash-es";
import { numberToCurrency, markFieldAsValid, markFieldAsInvalid, trackEvent, numberFormat, findPropertyTaxForZipCode, dollarConfig, percentageConfig, customDropdowns } from "./_calculators.js";
import "../vendor/charts";
import "../vendor/animated";
import "../vendor/select2.min";
import "./_serialize-object";
const $ = window.jQuery;
const dataLayer = window.dataLayer;

class CalculatorsMonthlyPayment {
  // Tracks error count
  errors = 0;
  totalPayment = 0;
  totalMonthlyPayment = 0;
  lastCalculation = {};

  // Initialize
  constructor() {
    customDropdowns();
    this.defineInputs();
    this.formHandler();
    this.createChart();
    this.initialState();
    this.wufooSubmit();
    this.averageIncomeFormVisibility();
  }

  // Advanced number inputs
  defineInputs() {
    // Monthly mortgage price inputs
    this.homePrice = new AutoNumeric("#home-price", dollarConfig);
    this.downPayment = new AutoNumeric("#down-payment", dollarConfig);
    this.loanTerm = $("#loan-term");
    this.zipCode = $("input#zip-code");
    this.interestRate = new AutoNumeric("#interest-rate", percentageConfig);

    // Average income assessment inputs
    this.email = $("input#lead-email");
    this.name = $("input#lead-name");
    this.carLoans = new AutoNumeric("input#lead-car-loans", dollarConfig);
    this.studentLoans = new AutoNumeric("input#lead-student-loans", dollarConfig);
    this.creditCards = new AutoNumeric("input#lead-credit-cards", dollarConfig);
    this.personalLoans = new AutoNumeric("input#lead-personal-loans", dollarConfig);
    this.phone = $("input#lead-phone");
  }

  // Default state for chart onload
  initialState() {
    $(() => {
      // Calculate onload for debugging purposes
      this.calculateMortgagePayments();

      // Fade-in onload
      $(".calculator").addClass("ready");
    });
  }

  // Average income is visible
  avgIncomeIsVisible() {
    return $(".calculator__avg-income").hasClass("visible");
  }

  // Estimate income needed for mortgage
  estimateIncomeForMortgage() {
    // Calculate salary range for current mortgage input
    let monthlyPayment = this.totalMonthlyPayment;
    let carLoans = parseInt(this.carLoans.rawValue);
    let studentLoans = parseInt(this.studentLoans.rawValue);
    let creditCards = parseInt(this.creditCards.rawValue);
    let personalLoans = parseInt(this.personalLoans.rawValue);
    let monthlyDebts = carLoans + studentLoans + creditCards + personalLoans;
    let totalMonthlyDebt = monthlyPayment + monthlyDebts;
    let lowSalary = round(totalMonthlyDebt / 0.33, 2) * 12;
    let lowSalaryUsd = numberToCurrency(lowSalary);
    let highSalary = round(totalMonthlyDebt / 0.28, 2) * 12;
    let highSalaryUsd = numberToCurrency(highSalary);

    // Populate UI and show report
    $('[data-salary="low"]').text(lowSalaryUsd);
    $('[data-salary="high"]').text(highSalaryUsd);

    // Already visible, re-show animation
    if (this.avgIncomeIsVisible()) {
      $('[data-salary="low"]').hide().text(lowSalaryUsd).fadeIn();
      $('[data-salary="high"]').hide().text(highSalaryUsd).fadeIn();
    }

    // First time appearance
    else {
      $('[data-salary="low"]').text(lowSalaryUsd);
      $('[data-salary="high"]').text(highSalaryUsd);
      $('[data-salary="average-income"]').addClass("visible");
    }
  }

  // Show/hide Average Income Assessment form
  averageIncomeFormVisibility() {
    // Click CTA button to open form
    let $element = $('[data-toggle="average-income-report"]');
    $element.on("click", (event) => {
      event.preventDefault();

      // Stop on :disabled
      let element = $(event.delegateTarget);
      if (element.is(":disabled")) return;

      $('[data-calculator="call-to-action"]').hide();

      $('[data-calculator="average-income-report"]').animate(
        {
          height: "toggle",
          opacity: "toggle",
        },
        500
      );
    });
  }

  // Wufoo API submit
  wufooSubmit() {
    // Submit handler
    let submitCount = 0;
    $('form[data-calculator="lead-form"]').on("submit", async (event) => {
      event.preventDefault();

      // Single submission only
      $('[data-calculator="lead-form"] button[type="submit"]').prop("disabled", true);

      // Clear any existing errors
      $("[data-error]").hide();

      // Wufoo API request
      try {
        let payload = {
          name: this.name.val(),
          email: this.email.val(),
          car_loans: this.carLoans.rawValue,
          student_loans: this.studentLoans.rawValue,
          credit_cards: this.creditCards.rawValue,
          personal_loans: this.personalLoans.rawValue,
          phone: this.phone.val(),
        };

        const wufooFormHash = "z1owitrz0f7job6";

        $("#lead-loading").addClass("visible");
        $("#lead-submit").removeClass("visible");

        let req = await $.ajax({
          type: "POST",
          url: `/api/wufoo/${wufooFormHash}`,
          data: JSON.stringify(payload),
          contentType: "application/json; charset=utf-8",
          dataType: "json",
        });

        // Wufoo errors
        if (req.result.Success === 0) {
          $('[data-error="wufoo"]').fadeIn();
          $('[data-calculator="lead-form"] button[type="submit"]').prop("disabled", false);
          return;
        }

        // Success
        $('form[data-calculator="lead-form"]').animate(
          {
            height: "toggle",
            opacity: "toggle",
          },
          500
        );

        trackEvent("Lead Form Submitted", "Monthly Mortgage Payment Calculator", window.location.pathname);

        this.estimateIncomeForMortgage();

        $("#lead-loading").removeClass("visible");
        $("#lead-submit").addClass("visible");

        $('[data-calculator="lead-form"] button[type="submit"]').prop("disabled", false);
      } catch (error) {
        // Catch errors
        // Unknown errors
        $('[data-error="general"]').fadeIn();
        $('[data-calculator="lead-form"] button[type="submit"]').prop("disabled", false);
        console.error("Form submission error", error);
        return;
      }
    });
  }

  // Calulator form validation
  calculatorFormIsValid() {
    // Input values
    let homePrice = this.homePrice.rawValue;
    let downPayment = this.downPayment.rawValue;
    let interestRate = this.interestRate.rawValue;
    let zipCode = this.zipCode.val();
    let loanTerm = this.loanTerm.val();

    // Required fields are empty
    if (some([homePrice, downPayment, interestRate, zipCode, loanTerm], isEmpty)) return false;

    // Invalid zip code
    if (zipCode.length !== 5) return false;

    // Invalid number
    if (homePrice <= 0 || downPayment <= 0 || interestRate <= 0) return false;

    return true;
  }

  // Calculate mortgage payments
  async calculateMortgagePayments() {
    // Verify required fields for calculation
    if (!this.calculatorFormIsValid()) return;

    // Input values
    let homePrice = this.homePrice.rawValue;
    let downPayment = this.downPayment.rawValue;
    let interestRate = this.interestRate.rawValue;
    let zipCode = this.zipCode.val();
    let loanTerm = this.loanTerm.val();

    // Calculated values
    let principal = homePrice - downPayment;
    let pmi = lt((downPayment / homePrice) * 100, 20) ? 0.5 : 0;
    let monthlyPMIPayment = 0;
    if (pmi > 0) {
      let monthlyPmiRate = pmi / 100 / 12;
      monthlyPMIPayment = round(monthlyPmiRate * principal, 2);
    }

    // Convert interest from a percentage to a decimal, and convert from
    // an annual rate to a monthly rate. Convert payment period in years
    // to the number of monthly payments.
    let monthlyInterestRate = interestRate / 100 / 12;
    let totalMonthlyPaymentCount = loanTerm * 12;
    let monthlyInterestPayment = monthlyInterestRate * principal;

    // Now compute the monthly payment figure, using esoteric math.
    let x = Math.pow(1 + monthlyInterestRate, totalMonthlyPaymentCount);
    let monthlyPaymentFigure = (principal * x * monthlyInterestRate) / (x - 1);

    // Check that the result is a finite number. If so, display the results.
    if (!isNumber(monthlyPaymentFigure)) {
      console.error("monthlyPaymentFigure is not a valid number", monthlyPaymentFigure);
      return false;
    }

    // Format result
    let monthlyPayment = round(monthlyPaymentFigure, 2);
    let monthlyInterest = round(monthlyInterestPayment, 2);
    let monthlyPrincipal = round(monthlyPayment - monthlyInterest, 2);
    let totalPayment = round(monthlyPaymentFigure * totalMonthlyPaymentCount, 2);
    let totalInterest = round(monthlyPaymentFigure * totalMonthlyPaymentCount - principal, 2);
    let zipCodeInput = this.zipCode.val();
    let taxPerThousand = await findPropertyTaxForZipCode(zipCodeInput);
    let assessedValue = round(homePrice * 0.9, 2);
    let annualPropertyTaxes = (assessedValue / 1000) * taxPerThousand;
    let estimatedTaxes = round(annualPropertyTaxes / 12, 2);

    // Global storage for other methods
    this.totalPayment = totalPayment;

    let currentCalculation = {
      monthlyPrincipal: monthlyPrincipal,
      monthlyInterest: monthlyInterest,
      estimatedTaxes: estimatedTaxes,
      monthlyPMIPayment: monthlyPMIPayment,
    };

    // Test for duplicate
    if (isEqual(this.lastCalculation, currentCalculation)) return;

    // Debug helpers
    console.log(`Tax rate per $1000 for ${zipCode} is ${taxPerThousand}.`);

    // Track analytics event: category, action, label
    const eventAction = zipCode;
    const eventValue = "$" + numberFormat(round(homePrice - downPayment, 2));
    const eventLabel = "$" + numberFormat(round(monthlyPayment + estimatedTaxes, 2));

    trackEvent("Mortgage Calculator Submitted", eventAction, eventLabel, eventValue);

    // Store as last submission
    this.lastCalculation = currentCalculation;

    // Update chart
    this.updateChart(monthlyPrincipal, monthlyInterest, estimatedTaxes, monthlyPMIPayment);

    // If user has already seen an estimate income for a mortgage, re-calculate it when the monthly mortgage price changes
    if (this.avgIncomeIsVisible()) this.estimateIncomeForMortgage();
  }

  // Submit calculator form
  submitForm = (event) => {
    let element = $(event.delegateTarget);
    let id = element.attr("id");
    let value;

    // #home-price
    if (id === "home-price") {
      value = this.homePrice.rawValue;
      if (!value || value <= 0) {
        markFieldAsInvalid(element);
        return;
      } else {
        markFieldAsValid(element);
      }
    }

    // #down-payment
    if (id === "down-payment") {
      value = this.downPayment.rawValue;
      if (!value || value <= 0) {
        markFieldAsInvalid(element);
        return;
      } else {
        markFieldAsValid(element);
      }
    }

    // #interest-rate
    if (id === "interest-rate") {
      value = this.interestRate.rawValue;
      if (!value || value <= 0) {
        markFieldAsInvalid(element);
        return;
      } else {
        markFieldAsValid(element);
      }
    }

    // #zip-code
    if (id === "zip-code") {
      value = this.zipCode.val();
      if (!value || value.length < 5) {
        markFieldAsInvalid(element);
        return;
      } else {
        markFieldAsValid(element);
      }

      let isRIzip = includes(["028", "029"], value.substring(0, 3));
      let isMAzip = includes(["010", "011", "012", "013", "014", "015", "016", "017", "018", "019", "020", "021", "022", "023", "024", "025", "026", "027"], value.substring(0, 3));
      if (!isRIzip && !isMAzip) {
        $("#out-of-service-area").fadeIn();
      } else {
        $("#out-of-service-area").fadeOut();
      }
    }

    // Calculate results
    this.calculateMortgagePayments();
  };

  // Form submit handler
  formHandler() {
    // Submit handler
    $('form[data-calculator="monthly-payment"]').on("submit", (event) => {
      event.preventDefault();
    });

    // Auto-submit on valid changes
    const $inputs = $('form[data-calculator="monthly-payment"] input, form[data-calculator="monthly-payment"] select');
    $inputs.on("change", (event) => {
      event.preventDefault();
      this.submitForm(event);
    });
  }

  // Update chart values
  updateChart(monthlyPrincipal, monthlyInterest, estimatedTaxes, pmi, preview) {
    // When we have a valid monthly price that's not 0
    if (!preview) {
      if (monthlyPrincipal > 0) {
        $('[data-calculator="call-to-action"]').addClass("visible");
        $('[data-toggle="average-income-report"]').prop("disabled", false);
      } else {
        $('[data-calculator="call-to-action"]').removeClass("visible");
        $('[data-toggle="average-income-report"]').prop("disabled", true);
      }
    }

    // Create initial data values
    if (this.chart.data.length === 0) {
      this.chart.data = [
        {
          label: "Principal",
          cost: monthlyPrincipal,
          color: "#00529f",
        },
        {
          label: "Interest",
          cost: monthlyInterest,
          color: "#ffdc00",
        },
        {
          label: "Taxes",
          cost: estimatedTaxes,
          color: "#2ECC40",
        },
        {
          label: "PMI",
          cost: pmi,
          color: "#DC454F",
        },
      ];
    }

    // Update existing values
    else {
      this.chart.data[0].cost = monthlyPrincipal;
      this.chart.data[1].cost = monthlyInterest;
      this.chart.data[2].cost = estimatedTaxes;
      this.chart.data[3].cost = pmi;
      this.chart.invalidateRawData();
    }

    // Total monthly payment
    if (!preview) {
      let existingPayment = $("#monthly-payment__monthly-payment").text();
      this.totalMonthlyPayment = monthlyPrincipal + monthlyInterest + estimatedTaxes;
      let totalFormatted = "$" + numberFormat(this.totalMonthlyPayment, 2, ".", ",");
      if (totalFormatted !== existingPayment) {
        $("#mortgage-monthly-payment").hide().text(totalFormatted).delay(500).fadeIn(1000);
      }
    } else {
      $("#mortgage-monthly-payment").fadeIn();
    }
  }

  // Display chart
  createChart() {
    am4core.ready(() => {
      am4core.useTheme(am4themes_animated);

      // Container
      var container = am4core.create("monthly-payment-chart", am4core.Container);
      container.width = am4core.percent(100);
      container.height = am4core.percent(100);
      container.layout = "horizontal";

      // Chart
      this.chart = container.createChild(am4charts.PieChart);

      // Doughnut cutout
      this.chart.radius = am4core.percent(80);
      this.chart.innerRadius = am4core.percent(30);
      this.chart.paddingTop = 0;
      this.chart.fontSize = 14;
      this.chart.focusable = false;

      // Legend
      this.chart.legend = new am4charts.Legend();
      //this.chart.legend.labels.template.text = "[bold]{category}[/] ${value}";
      this.chart.legend.itemContainers.template.paddingTop = 5;
      this.chart.legend.itemContainers.template.paddingBottom = 5;
      this.chart.legend.itemContainers.template.clickable = false;
      this.chart.legend.itemContainers.template.focusable = false;
      this.chart.legend.itemContainers.template.cursorOverStyle = am4core.MouseCursorStyle.default;
      this.chart.legend.markers.template.width = 16;
      this.chart.legend.markers.template.height = 16;

      // Series
      this.series = this.chart.series.push(new am4charts.PieSeries());

      // Animations
      this.series.hiddenState.properties.endAngle = -90;
      this.series.hiddenState.properties.startAngle = -90;
      this.series.hiddenState.transitionDuration = 1000;
      this.series.defaultState.transitionDuration = 1000;
      this.series.interpolationDuration = 1000;

      // Labels
      //this.series.labels.template.fill = am4core.color( "#3d3d3d" );
      //this.series.labels.template.radius = 10;
      this.series.labels.template.disabled = true;
      this.series.ticks.template.disabled = true;
      this.series.alignLabels = false;
      this.series.legendSettings.labelText = "[bold]{category}[/]";
      this.series.legendSettings.valueText = "${value}";
      this.series.legendSettings.align = "left";

      // Legend
      this.series.dataFields.value = "cost";
      this.series.dataFields.depthValue = "cost";
      this.series.dataFields.category = "label";

      // Colors
      this.series.colors.list = [am4core.color("#00529f"), am4core.color("#FFDC00"), am4core.color("#95C12A"), am4core.color("#4D9DE0"), am4core.color("#D32A2A"), am4core.color("#FA7921")];

      // Styles
      this.series.slices.template.stroke = am4core.color("#fff");
      this.series.slices.template.strokeWidth = 3;
      this.series.slices.template.strokeOpacity = 1;
      this.series.slices.template.cornerRadius = 5;
      this.series.slices.template.tooltipText = "";
      this.series.slices.template.clickable = false;
      this.series.slices.template.hoverable = false;
      this.series.slices.template.focusable = false;
      this.series.slices.template.clickable = false;
      this.series.slices.template.wheelable = false;
      this.series.slices.template.togglable = false;
      this.series.slices.template.trackable = false;
      this.series.slices.template.swipeable = false;
      this.series.slices.template.interactionsEnabled = false;
      this.series.slices.template.tabindex = 99999;

      // States
      var hoverState = this.series.slices.template.states.create("hover");
      hoverState.properties.scale = 1;
      var activeState = this.series.slices.template.states.create("active");
      activeState.properties.shiftRadius = 0;

      // Initial display data
      this.updateChart(530, 867, 388, 0, true);
    }); // end am4core.ready()
  }
}

new CalculatorsMonthlyPayment();
